import {
  Building2,
  Clapperboard,
  Megaphone,
  TicketCheck
} from "lucide-react";
import { AmbassadorClassCard } from "./components/AmbassadorClassCard";
import {
  AmbassadorAccountCard,
  AmbassadorAccountProvider,
  PartnerDashboard
} from "./components/PartnerDashboard";
import type { AmbassadorClass } from "./types";
import { PublicProposal } from "./components/ProposalWorkspace";

const ambassadorClasses: AmbassadorClass[] = [
  {
    title: "Publisher Ambassador",
    icon: Clapperboard,
    summary:
      "Enroll content owners, creators, shows, series, and event publishers into the XPress publishing and monetization system.",
    commissionBasis: [
      "Platform subscriptions for referred publishers",
      "Platform+ subscriptions for referred publishers",
      "Future approved publisher monetization packages"
    ],
    activeSurfaces: [
      "Publisher onboarding",
      "Content submission education",
      "XPress video monetization",
      "XPress Display promotion for shows, releases, and events",
      "Platform / Platform+ upgrade path"
    ],
    plannedSurfaces: [
      "Publisher-specific group request workflow",
      "Self-sold sponsor group request workflow",
      "API/embed eligibility request flow",
      "Publisher dashboard for referred account status"
    ],
    training: [
      "Publisher submission basics",
      "Approved library projection",
      "Custom VAST/VMAP eligibility",
      "Platform / Platform+ subscription pitch"
    ]
  },
  {
    title: "Advertising Ambassador",
    icon: Megaphone,
    summary:
      "Sell XPress video, RON, Display, venue, creator, and live-event advertising packages to approved buyers.",
    commissionBasis: [
      "Eligible video campaign sales",
      "Eligible RON campaign sales",
      "Eligible XPress Display campaign sales",
      "Advertiser-linked Platform / Platform+ subscriptions"
    ],
    activeSurfaces: [
      "XPress video campaigns",
      "Run-of-network campaigns",
      "XPress Display campaigns",
      "Venue/display packages",
      "Marketplace credit education"
    ],
    plannedSurfaces: [
      "Campaign quote/request workflow",
      "Starter campaign credit enrollment",
      "Direct VAST/VMAP URL package handoff",
      "Advertiser proof/reconciliation packet"
    ],
    training: [
      "Video campaign basics",
      "RON positioning",
      "Display package pricing",
      "Marketplace credit rules"
    ]
  },
  {
    title: "Venue Ambassador",
    icon: Building2,
    summary:
      "Enroll venues as distribution points, display inventory partners, local sponsor surfaces, and future device locations.",
    commissionBasis: [
      "Eligible net venue ad revenue",
      "Venue display package revenue",
      "Future approved venue device/support packages"
    ],
    activeSurfaces: [
      "Venue onboarding",
      "XLNT Sports venue value",
      "Local sponsor display packages",
      "QR/event/poster promotions",
      "Venue-as-display-publisher path"
    ],
    plannedSurfaces: [
      "Venue screen inventory dashboard",
      "Device/screen status",
      "Venue-specific display placement catalog",
      "Local sponsor campaign builder"
    ],
    training: [
      "Venue setup story",
      "Screen inventory basics",
      "Local sponsor package examples",
      "Venue revenue attribution"
    ]
  },
  {
    title: "Entitlements Ambassador",
    icon: TicketCheck,
    summary:
      "Support future membership, access, PPV, ticketing, and rights-based revenue products.",
    commissionBasis: [
      "Future entitlement sales",
      "Future subscription/access activations",
      "Future rights-based product packages"
    ],
    activeSurfaces: [
      "Program education",
      "Lead capture for entitlement-ready partners",
      "Future product waitlist"
    ],
    plannedSurfaces: [
      "PPV/live-event access packages",
      "Membership and gated content products",
      "Ticketing-style entitlement packages",
      "Entitlement revenue dashboard"
    ],
    training: [
      "Entitlement product overview",
      "Approved claims and limitations",
      "Future access-product workflow"
    ]
  }
];

export function App() {
  const route = window.location.pathname.replace(/\/+$/, "") || "/";
  return (
    <AmbassadorAccountProvider>
      {route === "/dashboard" ? <DashboardPage /> : route.startsWith("/proposal/") ? <PublicProposal token={decodeURIComponent(route.slice("/proposal/".length))} /> : <LandingPage />}
    </AmbassadorAccountProvider>
  );
}

function LandingPage() {
  return (
    <main className="app">
      <section className="hero">
          <div className="hero__inner">
            <div className="hero__copy">
              <a className="ambassador-wordmark" href="/" aria-label="Ambassador by XLNT Ventures home">
                <span>Ambassador</span>
                <small>by XLNT Ventures</small>
              </a>
              <h1>Develop the relationships that move ventures forward.</h1>
              <p>
                Ambassador is the partner-development platform of XLNT Ventures
                &amp; Acquisitions, Corp.
              </p>
            </div>

            <AmbassadorAccountCard showDashboardLink />
          </div>
      </section>

      <section className="section">
        <div className="section-heading">
          <h2>Current practices</h2>
          <p>
            The initial four practices serve the XPress product family. Expand a
            practice to review its current offering, commission basis, planned
            integrations, and training requirements.
          </p>
        </div>

        <div className="class-list">
          {ambassadorClasses.map((ambassadorClass) => (
            <AmbassadorClassCard key={ambassadorClass.title} ambassadorClass={ambassadorClass} />
          ))}
        </div>
      </section>
      <footer className="site-footer">
        <div>
          <span className="site-footer__mark">Ambassador</span>
          <p>A partner-development platform of XLNT Ventures &amp; Acquisitions, Corp.</p>
        </div>
      </footer>
    </main>
  );
}

function DashboardPage() {
  return (
    <main className="app">
      <section className="dashboard-hero">
        <div className="dashboard-hero__inner">
          <div className="dashboard-hero__copy">
            <a className="back-link" href="/">← Program home</a>
          </div>
        </div>
      </section>
      <PartnerDashboard />
    </main>
  );
}
